import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Calendar, MapPin, ArrowRight } from 'lucide-react';
import { cityConfig } from '@/config/cities';

const QARGO_IMAGE = "https://customer-assets.emergentagent.com/job_paris-hearts-hunt/artifacts/mu14b8i4_qargo%20-%20officiel.jpg";

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-[calc(100vh-10rem)]" data-testid="about-page">
      
      {/* HERO HEADER */}
      <div 
        className="relative py-20 px-6 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(230, 57, 70, 0.15) 0%, rgba(10, 10, 11, 1) 50%, rgba(39, 39, 42, 0.3) 100%)'
        }}
      >
        {/* Decorative hearts in background */}
        <div className="absolute inset-0 overflow-hidden opacity-5">
          <Heart className="absolute top-10 left-10 w-32 h-32 text-[#E63946]" fill="#E63946" />
          <Heart className="absolute bottom-10 right-20 w-24 h-24 text-[#E63946]" fill="#E63946" />
          <Heart className="absolute top-1/2 right-10 w-16 h-16 text-[#E63946]" fill="#E63946" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center relative z-10"
        >
          <Heart className="w-12 h-12 text-[#E63946] mx-auto mb-6 heart-pulse" fill="#E63946" />
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">HE</span>
            <span className="text-[#E63946]">ART</span>
            <span className="text-white">S</span>
            <span className="text-[#A1A1AA]"> IN {cityConfig.name.toUpperCase()}</span>
          </h1>
          <p className="text-lg sm:text-xl text-[#A1A1AA] max-w-xl mx-auto mb-6">
            Quand l'art s'invite dans le quotidien
          </p>
          <a
            href="https://www.instagram.com/qargo.art/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 px-5 py-2.5 border border-[#E63946]/40 hover:border-[#E63946] bg-[#E63946]/10 hover:bg-[#E63946]/20 transition-all duration-300 rounded-sm group"
            data-testid="hero-instagram-link"
          >
            <svg className="w-4 h-4 text-[#E63946]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            <span className="text-sm text-white/80 group-hover:text-white transition-colors">Suivre</span>
            <span className="text-sm text-[#E63946] font-semibold">@qargo.art</span>
          </a>
        </motion.div>
      </div>

      {/* KEY STATS BAND */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-[#141416] border-y border-[#27272A] py-6 px-6"
      >
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-8 sm:gap-16">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-[#E63946]" fill="#E63946" />
            <div>
              <div className="text-2xl sm:text-3xl font-display font-bold text-white">{cityConfig.totalHearts}</div>
              <div className="text-xs uppercase tracking-wider text-[#A1A1AA]">cœurs</div>
            </div>
          </div>
          <div className="w-px h-12 bg-[#27272A]" />
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-[#E63946]" />
            <div>
              <div className="text-2xl sm:text-3xl font-display font-bold text-white">{cityConfig.date}</div>
              <div className="text-xs uppercase tracking-wider text-[#A1A1AA]">{cityConfig.event}</div>
            </div>
          </div>
          <div className="w-px h-12 bg-[#27272A] hidden sm:block" />
          <div className="hidden sm:flex items-center gap-3">
            <MapPin className="w-6 h-6 text-[#E63946]" />
            <div>
              <div className="text-2xl sm:text-3xl font-display font-bold text-white">{cityConfig.name}</div>
              <div className="text-xs uppercase tracking-wider text-[#A1A1AA]">France</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* MAIN CONTENT */}
      <div className="px-6 py-16">
        <div className="max-w-3xl mx-auto">

          {/* SECTION 1: LE CONCEPT */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-20"
          >
            <h2 className="text-xs tracking-[0.3em] uppercase text-[#E63946] font-bold mb-8">
              Le Concept
            </h2>
            
            {/* Big opening quote */}
            <div className="relative mb-10 pl-6 border-l-4 border-[#E63946]">
              <p className="text-xl sm:text-2xl text-white font-light leading-relaxed">
                À l'occasion {cityConfig.id === 'paris' ? 'de la Journée mondiale de l\'art' : `du ${cityConfig.event}`}, l'artiste <span className="text-[#E63946] font-medium">qargo</span> a disséminé {cityConfig.totalHearts} cœurs en briques LEGO dans les rues de {cityConfig.name}.
              </p>
            </div>
            
            <div className="space-y-6 text-[#A1A1AA] leading-relaxed text-justify">
              <p>
                Ces objets sont volontairement laissés dans l'espace public, sans indication ni mise en scène. Ils apparaissent dans la ville comme n'importe quel élément du quotidien, avec une seule différence : ils peuvent être trouvés.
              </p>

              <p>
                Le projet repose sur cette rencontre imprévisible. Il ne s'agit pas de chercher, mais de tomber dessus, de s'arrêter, et de vivre un moment qui n'était pas prévu.
              </p>

              {/* Highlighted card */}
              <div className="bg-[#141416] border border-[#27272A] p-6 my-8">
                <p className="text-white">
                  Chaque cœur est conçu comme un geste simple. Sa forme est immédiatement reconnaissable, et son intention est directe : <span className="text-[#E63946]">provoquer une émotion, créer du plaisir, apporter une présence positive</span> dans le flux de la ville.
                </p>
              </div>

              <p>
                Mais dans une ville où tout va vite, où l'attention est constamment sollicitée, ces objets posent aussi une autre question : <span className="text-white font-medium">que voit-on encore réellement autour de soi ?</span>
              </p>

              <p>
                Beaucoup passeront à côté sans les remarquer. Non pas parce qu'ils sont invisibles, mais parce que le regard est ailleurs, absorbé par le rythme quotidien. Trouver un cœur devient alors une expérience personnelle. C'est s'autoriser, même brièvement, à sortir du flux, à s'arrêter, à prêter attention à quelque chose d'inattendu.
              </p>

              <p>
                Ce moment peut se prolonger. Le cœur devient alors un objet que l'on garde avec soi, comme un signe, un porte-bonheur, quelque chose qui accompagne la suite.
              </p>

              <p>
                Le partager permet de prolonger cette expérience, en la faisant exister au-delà de soi, avec la même intention initiale : transmettre quelque chose de simple, de positif, de vivant.
              </p>

              {/* Final quote - emphasized */}
              <div className="relative py-8 my-8">
                <div className="absolute left-0 top-0 text-6xl text-[#E63946]/20 font-serif leading-none">"</div>
                <p className="text-lg sm:text-xl text-white italic pl-8 pr-4">
                  Avec {cityConfig.title}, qargo propose un geste discret, mais profondément humain : rappeler que l'art peut surgir dans le quotidien et y introduire, sans prévenir, une forme de plaisir, d'émotion, et une manière différente d'apprécier ce qui nous entoure.
                </p>
                <div className="absolute right-0 bottom-0 text-6xl text-[#E63946]/20 font-serif leading-none">"</div>
              </div>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 mb-20">
            <div className="w-16 h-px bg-[#27272A]" />
            <Heart className="w-4 h-4 text-[#E63946]" fill="#E63946" />
            <div className="w-16 h-px bg-[#27272A]" />
          </div>

          {/* SECTION 2: L'ARTISTE */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-xs tracking-[0.3em] uppercase text-[#E63946] font-bold mb-10">
              L'Artiste
            </h2>

            {/* Artist Photo & Quote */}
            <div className="flex flex-col md:flex-row items-center gap-10 mb-12">
              {/* Photo with glow */}
              <div 
                className="w-48 h-48 md:w-56 md:h-56 flex-shrink-0 overflow-hidden relative"
                style={{
                  boxShadow: '0 0 60px rgba(230, 57, 70, 0.3), 0 0 100px rgba(230, 57, 70, 0.1)'
                }}
              >
                <div className="absolute inset-0 border-2 border-[#E63946]" />
                <img
                  src={QARGO_IMAGE}
                  alt="qargo"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Quote */}
              <div className="text-center md:text-left flex-1">
                <h3 className="font-display text-4xl sm:text-5xl font-bold mb-6 lowercase text-white">
                  qargo
                </h3>
                <div className="relative">
                  <span className="absolute -left-4 -top-4 text-5xl text-[#E63946] font-serif">"</span>
                  <blockquote className="text-lg sm:text-xl text-[#A1A1AA] italic leading-relaxed">
                    Je n'utilise plus le LEGO pour jouer, mais pour créer du sens et du lien. Le QR code me permet d'en démultiplier la portée.
                  </blockquote>
                  <span className="absolute -right-2 bottom-0 text-5xl text-[#E63946] font-serif">"</span>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className="space-y-5 text-[#A1A1AA] leading-relaxed text-justify">
              <p>
                Artiste plasticien contemporain, <span className="text-white font-medium">qargo</span> utilise exclusivement les briques LEGO pour créer des œuvres inspirées de figures, d'objets et de symboles emblématiques de la culture pop. Portraits iconiques, artefacts culturels, références collectives : chaque création repose sur des formes immédiatement reconnaissables, capables d'activer une mémoire commune et une émotion personnelle.
              </p>

              <p>
                Son travail ne s'arrête pas à ce que l'on voit. Le QR code, intégré au cœur de ses œuvres, permet d'aller au-delà de l'objet physique. Par un geste simple, il ouvre l'accès à des histoires inspirantes, à des parcours singuliers et aux valeurs humaines portées par les personnages ou les objets représentés. Cette passerelle entre le tangible et le digital crée un lien direct, accessible à tous, sans hiérarchie de génération ou de culture.
              </p>

              <p>
                Cette démarche s'est affirmée au fil du temps, nourrie par l'amitié et les échanges réguliers avec <span className="text-white font-medium">Masahiro Hara</span>, inventeur du QR code, dont le parcours a profondément marqué la vision artistique de qargo. Leur relation a renforcé une conviction essentielle : <span className="text-[#E63946]">une œuvre peut être à la fois simple dans sa forme et profonde dans ce qu'elle transmet</span>.
              </p>

              <p>
                Ancrée dans la culture pixel et vidéoludique, marquée par la culture japonaise et les icônes qui traversent les générations, l'œuvre de qargo est lisible, exigeante et profondément humaine. Chaque création propose un double regard : celui que l'on porte sur l'objet, et celui que l'on prolonge par ce qu'il raconte.
              </p>
            </div>

          </motion.section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-20 text-center"
          >
            <div className="bg-gradient-to-r from-transparent via-[#27272A] to-transparent h-px mb-12" />
            
            <p className="text-[#A1A1AA] mb-6">
              Tu as trouvé un cœur ?
            </p>
            
            <button
              onClick={() => navigate('/find')}
              className="group bg-[#E63946] text-white font-bold tracking-wider uppercase px-10 py-4 inline-flex items-center gap-3 hover:-translate-y-1 transition-all duration-300"
              style={{ boxShadow: '0 4px 30px rgba(230, 57, 70, 0.4)' }}
            >
              <Heart className="w-5 h-5" fill="white" />
              Partager ma découverte
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default About;
